import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';

import { Chip, CircularProgress, Grid, IconButton, TextField, Typography } from '@material-ui/core';

import AddIcon from '@material-ui/icons/Add';

import MyAutoComplete from '../utils/MyAutoComplete';
import MyTextField from '../utils/MyTextField';
import UnoButton from '../utils/UnoButton';
import { myLabelInput, unoTheme } from '../../assets/styles/unoTheme';
import { FundsManagersAdministratorsAPI } from '../../API/funds_managers_administrators';

const useStyles = makeStyles((theme) => ({
    contentModal: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        paddingBottom: theme.spacing(4),
    },
    actionsFlex: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    actionsFlexRight: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    dialogTitle: {
        '& h2': {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
        }
    },
    label: myLabelInput,
    entityName: {
        color: unoTheme.mainColor,
        fontWeight: '500',
    },
    bold: {
        fontWeight: 'bold',
    },
    namesHint: {
        fontSize: '12px',
        color: unoTheme.defaultTextColor,
        marginBottom: theme.spacing(1),
    },
    namesList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    namesEmpty: {
        fontSize: '12px',
        fontStyle: 'italic',
        color: unoTheme.defaultTextColor,
        marginBottom: theme.spacing(1),
    },
    namesForm: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    namesMessage: {
        fontSize: '12px',
        color: unoTheme.redMoney,
        marginTop: theme.spacing(1),
    },
    namesProgress: {
        display: 'flex',
        justifyContent: 'center',
        color: unoTheme.mainColor,

        '& .MuiCircularProgress-root': {
            width: '20px !important',
            height: '20px !important',
        },
    },
}));

//Mesmo limite do VARCHAR(255) das tabelas de nomes alternativos
const MAX_NAME_LENGTH = 255;

//As duas entidades seguem o mesmo fluxo, mudando apenas os endpoints. As chaves precisam
//continuar iguais às constantes MANAGERS/ADMINISTRATORS de InstituitionsScreen
const NAMES_API = {
    managers: {
        list: (entityId) => FundsManagersAdministratorsAPI.getManagerNames(entityId),
        create: (entityId, name) => FundsManagersAdministratorsAPI.createManagerName(entityId, name),
        remove: (nameId) => FundsManagersAdministratorsAPI.deleteManagerName(nameId),
    },
    administrators: {
        list: (entityId) => FundsManagersAdministratorsAPI.getAdministratorNames(entityId),
        create: (entityId, name) => FundsManagersAdministratorsAPI.createAdministratorName(entityId, name),
        remove: (nameId) => FundsManagersAdministratorsAPI.deleteAdministratorName(nameId),
    },
};

/**
 * Modal de vínculo de uma instituição a um gestor ou administrador de fundos.
 * @param {boolean} open
 * @param {string} title título do modal
 * @param {string} entityName nome do gestor/administrador sendo vinculado
 * @param {string} entityLabel rótulo do campo (default 'Registro'; no modo lote, 'Registros')
 * @param {number} currentInstituitionId instituição já vinculada (pré-seleciona e habilita o desvincular)
 * @param {boolean} allowUnlink força a exibição do desvincular mesmo sem currentInstituitionId (modo lote)
 * @param {array} instituitions instituições disponíveis para seleção
 * @param {string} entity 'managers' ou 'administrators'; define os endpoints dos nomes alternativos
 * @param {number} entityId id do registro; quando ausente (modo lote) a seção de nomes não aparece
 * @param {boolean} canEditNames libera adicionar/remover nomes alternativos
 * @param {function} handleClose
 * @param {function} handleSelect recebe o id da instituição, ou null para desvincular
 * @param {function} handleNamesChanged avisa o pai que os nomes mudaram, para recarregar a lista
 */
export default function LinkInstituitionModal(props) {

    const classes = useStyles();

    const { open, title, entityName, entityLabel, currentInstituitionId, allowUnlink,
        instituitions, entity, entityId, canEditNames,
        handleClose, handleSelect, handleNamesChanged } = props;

    const [instituitionSelected, setInstituitionSelected] = useState(null);

    //Nomes alternativos do registro (só no vínculo individual)
    const [names, setNames] = useState([]);
    const [newName, setNewName] = useState('');
    const [namesLoading, setNamesLoading] = useState(false);
    const [namesMessage, setNamesMessage] = useState('');

    const namesApi = entity ? NAMES_API[entity] : null;

    useEffect(() => {

        if (open) {

            //Pré-seleciona a instituição já vinculada, para o usuário ver o vínculo atual
            const current = currentInstituitionId && instituitions ?
                instituitions.find(el => el.id == currentInstituitionId) : null;

            setInstituitionSelected(current || null);

        } else {

            setInstituitionSelected(null);
        }

    }, [open, currentInstituitionId, instituitions])

    const loadNames = async (idToLoad) => {

        setNamesLoading(true);

        const response = await NAMES_API[entity].list(idToLoad);

        if (response.success) {

            setNames(response.body.rows);

        } else {

            setNamesMessage('Falha ao carregar os nomes alternativos');
        }

        setNamesLoading(false);
    }

    useEffect(() => {

        setNewName('');
        setNamesMessage('');

        if (open && entityId && NAMES_API[entity]) {

            loadNames(entityId);

        } else {

            setNames([]);
        }

    }, [open, entityId, entity])

    //Nomes são gravados no ato: 'Cancelar' fecha o modal mas não os desfaz, porque cancela
    //apenas o vínculo de instituição. Encadeá-los no 'Confirmar' não funcionaria, já que ele
    //fecha o modal pelo callback do pai
    const doCreateName = async () => {

        const nameToCreate = newName.trim();

        if (!nameToCreate) {
            return;
        }

        setNamesMessage('');

        const response = await namesApi.create(entityId, nameToCreate);

        if (response.success) {

            setNewName('');
            await loadNames(entityId);

            if (handleNamesChanged) handleNamesChanged();

        } else {

            //Mensagem inline, e não pelo alerta global: o modal continua aberto e o erro
            //(nome já usado por outro registro, por exemplo) pertence ao contexto dele
            setNamesMessage(response.body && response.body.message ?
                response.body.message : 'Erro ao cadastrar o nome alternativo');
        }
    }

    const doDeleteName = async (nameId) => {

        setNamesMessage('');

        const response = await namesApi.remove(nameId);

        if (response.success) {

            await loadNames(entityId);

            if (handleNamesChanged) handleNamesChanged();

        } else {

            setNamesMessage(response.body && response.body.message ?
                response.body.message : 'Erro ao remover o nome alternativo');
        }
    }

    return (
        <Dialog
            fullWidth={true}
            maxWidth={'sm'}
            open={open}
            onClose={() => handleClose()}>
            <DialogTitle
                className={classes.dialogTitle}>
                {title}
            </DialogTitle>

            <DialogContent
                className={classes.contentModal}>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Typography className={classes.label}>
                            {entityLabel ? entityLabel : 'Registro'}
                        </Typography>
                        <Typography className={classes.entityName}>
                            {entityName}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                        <MyAutoComplete
                            outerlabel={"Instituição"}
                            onChange={(event, newValue) => setInstituitionSelected(newValue)}
                            options={instituitions ? instituitions : []}
                            autoComplete
                            label={'Código ou nome da instituição'}
                            filterOptions={(options, state) => {
                                if (!options) return [];
                                return options.filter(element => element.cod
                                    .includes(state.inputValue)
                                    || element.description.toLowerCase().includes(state.inputValue.toLowerCase()));
                            }}
                            value={instituitionSelected ? instituitionSelected : null}
                            getOptionLabel={(option) => option.description}
                            renderOption={(option) => (
                                <React.Fragment key={'link-instituition-' + option.id}>
                                    <span className={classes.bold}>{option.description}</span>
                                </React.Fragment>
                            )}
                            renderInput={(params) => <TextField {...params} margin="normal" variant="outlined" />}
                        />
                    </Grid>

                    {
                        //Só no vínculo individual: o modo lote não tem um registro único
                        //a que associar nomes
                        entityId && namesApi ?
                            <Grid item xs={12} sm={12} md={12} lg={12}>

                                <Typography className={classes.label}>
                                    {'Nomes alternativos'}
                                </Typography>

                                <Typography className={classes.namesHint}>
                                    {'Outros nomes que representam este mesmo registro quando o cadastro é cruzado com outra base'}
                                </Typography>

                                {
                                    namesLoading ?
                                        <div className={classes.namesProgress}>
                                            <CircularProgress disableShrink color="inherit" />
                                        </div>
                                        :
                                        <>
                                            {
                                                names.length ?
                                                    <div className={classes.namesList}>
                                                        {
                                                            names.map((el) => (
                                                                <Chip
                                                                    key={'entity-name-' + el.id}
                                                                    label={el.name}
                                                                    size="small"
                                                                    onDelete={canEditNames ?
                                                                        () => doDeleteName(el.id) : undefined} />
                                                            ))
                                                        }
                                                    </div>
                                                    :
                                                    <Typography className={classes.namesEmpty}>
                                                        {'Nenhum nome alternativo cadastrado'}
                                                    </Typography>
                                            }

                                            {
                                                canEditNames ?
                                                    <div className={classes.namesForm}>
                                                        <MyTextField
                                                            value={newName}
                                                            onChange={(value) => {
                                                                setNewName(value);
                                                                setNamesMessage('');
                                                            }}
                                                            onKeyPress={(e) => {
                                                                if (e.key === 'Enter') doCreateName();
                                                            }}
                                                            variant="outlined"
                                                            margin="dense"
                                                            placeholder="Informe outro nome para este registro"
                                                            inputProps={{ maxLength: MAX_NAME_LENGTH }}
                                                            type="text"
                                                            id="inputNewEntityName"
                                                            fullWidth />

                                                        <IconButton
                                                            aria-label="add new name"
                                                            aria-controls="primary-search-account-menu"
                                                            aria-haspopup="true"
                                                            color="inherit"
                                                            disabled={!newName.trim()}
                                                            onClick={() => doCreateName()}
                                                        >
                                                            <AddIcon />
                                                        </IconButton>

                                                    </div> : null
                                            }

                                            {
                                                namesMessage ?
                                                    <Typography className={classes.namesMessage}>
                                                        {namesMessage}
                                                    </Typography> : null
                                            }
                                        </>
                                }

                            </Grid> : null
                    }
                </Grid>

            </DialogContent>

            <DialogActions className={classes.actionsFlex}>

                <UnoButton onClick={() => handleClose()} color="secondary">
                    {'Cancelar'}
                </UnoButton>

                <div className={classes.actionsFlexRight}>

                    {
                        allowUnlink || currentInstituitionId ?
                            <UnoButton
                                onClick={() => handleSelect(null)}
                                type={'errorClean'}>
                                {'Desvincular'}
                            </UnoButton> : null
                    }

                    <UnoButton
                        disabled={!instituitionSelected}
                        onClick={() => handleSelect(instituitionSelected.id)}
                        color="primary">
                        {'Confirmar'}
                    </UnoButton>

                </div>

            </DialogActions>

        </Dialog>
    );
}
